/**
 * ============================================================================
 * GENA — « C'est un cadeau ? » au clic sur « Passer à la caisse »  (15/09/2026)
 * ----------------------------------------------------------------------------
 * CE QUE ÇA FAIT
 *   Au moment où la cliente valide son panier, on intercepte le départ vers le
 *   paiement, on pose la question du cadeau une seule fois, puis on relance le
 *   parcours exactement là où il s'était arrêté.
 *
 * POURQUOI PAS SUR LA FICHE PRODUIT (essayé, abandonné le 15/09)
 *   L'emballage est une option de COMMANDE. Posée par article, elle créait deux
 *   impasses relevées par Hugo : si le bijou est déjà au panier, aucun moyen
 *   d'ajouter l'emballage seul ; et une case par fiche permettait d'en cumuler
 *   plusieurs.
 *
 * POURQUOI PAS DANS LE PANIER DE VANGA
 *   Leur panier se re-rend en permanence (constaté : leurs propres offres
 *   disparaissent entre deux mesures). Toute injection chez eux serait effacée,
 *   et casserait à leur prochaine mise à jour. On écoute un clic, rien de plus.
 *
 * LE POINT TECHNIQUE QUI COMPTE
 *   Le bouton de Vanga vit dans un shadow DOM (`div#vanga-smartcart`, mode
 *   `open`). Quand l'événement en sort, `event.target` est **retargeté vers
 *   l'hôte** : un listener qui lit `event.target` ne verra jamais le bouton.
 *   Il faut parcourir `event.composedPath()`, qui traverse le shadow DOM.
 *   Vérifié en production le 09/09.
 *
 * RÈGLE ABSOLUE — ON NE BLOQUE JAMAIS UNE VENTE
 *   On n'intercepte que si TOUT est réuni : la modale existe, la question n'a
 *   pas déjà été posée, et le bouton est reconnu. Au moindre doute, le clic
 *   passe et le paiement suit son cours. Un filet de sécurité relance en plus
 *   le parcours si la modale ne s'ouvre pas.
 * ============================================================================
 */
(function () {
  'use strict';

  var CLE_SESSION = 'gena:cadeau-demande';
  var MOTS = /passer à la caisse|passer a la caisse|commander|checkout|payer|paiement|finaliser/i;

  var traitementEnCours = false;
  var boutonEnAttente = null;

  function dejaDemande() {
    try { return sessionStorage.getItem(CLE_SESSION) === '1'; }
    catch (e) { return false; } // navigation privée stricte : on ne demandera qu'une fois par page
  }

  function marquerDemande() {
    try { sessionStorage.setItem(CLE_SESSION, '1'); } catch (e) { /* sans effet */ }
  }

  function modale() {
    return document.querySelector('[data-gena-gift-modal]');
  }

  /** Le nœud cliqué mène-t-il au paiement ? On juge sur le sens, pas sur un sélecteur d'app. */
  function estBoutonPaiement(noeud) {
    if (!noeud || noeud.nodeType !== 1) return false;

    var balise = noeud.tagName;
    if (balise !== 'BUTTON' && balise !== 'A' && balise !== 'INPUT') return false;

    // Un lien ou un formulaire qui pointe vers /checkout.
    var href = noeud.getAttribute && (noeud.getAttribute('href') || '');
    if (href && /\/checkout/.test(href)) return true;
    if (noeud.name === 'checkout') return true;

    var texte = (noeud.innerText || noeud.textContent || noeud.value || '').trim();
    // Un texte trop long n'est pas un bouton : on évite d'attraper un conteneur.
    return texte.length > 0 && texte.length < 40 && MOTS.test(texte);
  }

  /** Relance le parcours interrompu, sans court-circuiter la logique de Vanga. */
  function relancer() {
    var bouton = boutonEnAttente;
    boutonEnAttente = null;
    marquerDemande();

    if (bouton && typeof bouton.click === 'function') {
      // Le drapeau est posé : ce second clic traversera sans être intercepté.
      setTimeout(function () { bouton.click(); }, 60);
    } else {
      window.location.href = '/checkout';
    }
  }

  /** Lit les champs de la modale et construit les propriétés de ligne. */
  function proprietes(boite) {
    var props = {};
    var lire = function (selecteur, repli) {
      var champ = boite.querySelector(selecteur);
      if (!champ || !champ.value || !champ.value.trim()) return;
      var etiquette = champ.closest('label') && champ.closest('label').querySelector('span');
      var nom = etiquette && etiquette.textContent ? etiquette.textContent.trim() : repli;
      props[nom] = champ.value.trim();
    };
    lire('[data-gift-recipient]', 'Destinataire');
    lire('[data-gift-sender]', 'De la part de');
    lire('[data-gift-message]', 'Votre mot');
    return props;
  }

  /** Ajoute l'emballage, puis relance le paiement — quoi qu'il arrive. */
  function accepter(boite) {
    var id = parseInt(boite.getAttribute('data-variant-id'), 10);
    if (!id) { relancer(); return; }

    var bouton = boite.querySelector('[data-gift-accept]');
    if (bouton) { bouton.disabled = true; bouton.setAttribute('aria-busy', 'true'); }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (panier) {
        // Déjà présent (ajouté depuis une autre session, ou double clic) : on n'en remet pas.
        var present = (panier.items || []).some(function (l) { return l.variant_id === id; });
        if (present) return null;
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1, properties: proprietes(boite) }] })
        });
      })
      .catch(function () { /* l'ajout a échoué : on laisse quand même partir au paiement */ })
      .then(function () {
        try { boite.close(); } catch (e) { /* déjà fermée */ }
        relancer();
      });
  }

  function ouvrir(boite) {
    try {
      if (typeof boite.showModal === 'function') boite.showModal();
      else boite.setAttribute('open', '');
      return boite.open === true || boite.hasAttribute('open');
    } catch (e) {
      return false;
    }
  }

  function brancherModale(boite) {
    if (boite.dataset.genaPret) return;
    boite.dataset.genaPret = '1';

    var accepte = boite.querySelector('[data-gift-accept]');
    if (accepte) {
      accepte.addEventListener('click', function () { accepter(boite); });
    }

    // Refus, croix de fermeture, touche Échap : tous passent par `close`.
    boite.addEventListener('close', function () {
      if (traitementEnCours) { traitementEnCours = false; relancer(); }
    });

    var zone = boite.querySelector('[data-gift-message]');
    var compteur = boite.querySelector('[data-gift-count]');
    if (zone && compteur) {
      zone.addEventListener('input', function () {
        compteur.textContent = String(zone.value.length);
      });
    }
  }

  document.addEventListener('click', function (ev) {
    try {
      if (dejaDemande() || traitementEnCours) return;

      var boite = modale();
      if (!boite) return; // section absente ou désactivée : on ne touche à rien

      var chemin = ev.composedPath ? ev.composedPath() : [ev.target];
      var bouton = null;
      for (var i = 0; i < chemin.length; i++) {
        if (estBoutonPaiement(chemin[i])) { bouton = chemin[i]; break; }
      }
      if (!bouton) return;

      // Ne pas intercepter un clic né DANS la modale.
      if (boite.contains(bouton)) return;

      brancherModale(boite);
      boutonEnAttente = bouton;
      traitementEnCours = true;

      if (!ouvrir(boite)) {
        // La modale n'a pas pu s'ouvrir : on n'a rien bloqué, le clic suit son cours.
        traitementEnCours = false;
        boutonEnAttente = null;
        return;
      }

      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
    } catch (e) {
      // Jamais au détriment du parcours d'achat.
      traitementEnCours = false;
      boutonEnAttente = null;
    }
  }, true); // capture : on passe avant le gestionnaire de Vanga

  var boiteInitiale = modale();
  if (boiteInitiale) brancherModale(boiteInitiale);
})();
